/**
 * Demo mode (`?demo`): the app runs on sample data in memory and must not touch anything real on this computer. This module
 * is imported before any other, so in demo mode every later read and write of localStorage, sessionStorage and IndexedDB goes
 * to throw-away memory: the real cloud connection, printer setup, device id and backup key cannot be changed from the demo.
 * The desktop shell's printing, backup and update calls are switched off in lib/desktop.ts.
 */
export const DEMO = new URLSearchParams(location.search).has("demo");

class MemoryStorage implements Storage {
  private readonly map = new Map<string, string>();
  get length() { return this.map.size; }
  clear() { this.map.clear(); }
  getItem(key: string) { return this.map.get(key) ?? null; }
  key(index: number) { return [...this.map.keys()][index] ?? null; }
  removeItem(key: string) { this.map.delete(key); }
  setItem(key: string, value: string) { this.map.set(key, String(value)); }
}

if (DEMO) {
  for (const name of ["localStorage", "sessionStorage"] as const) Object.defineProperty(window, name, { value: new MemoryStorage(), configurable: true });
  // Nothing in the demo stores to disk; a feature that would (backup password) simply reports that it is unavailable.
  Object.defineProperty(window, "indexedDB", { value: undefined, configurable: true });
}

/** Opens the demo in this window. The real session is kept: leaving the demo comes back to it. */
export const openDemo = () => {
  if (confirm("Open the demo?\n\nA sample restaurant opens in this window. Nothing you do there is saved, printed or sent to the cloud. Press \"Leave the demo\" to come back here.")) location.href = `${location.pathname}?demo`;
};
/** Leaves the demo: a fresh page, so demo data and a real session never share a window. */
export const leaveDemo = () => { location.href = location.pathname; };
